'use client'
import type { Product, Variant } from '@/payload-types'

import { RichText } from '@/components/RichText'
import { AddToCart } from '@/components/Cart/AddToCart'
import { Price } from '@/components/Price'
import React, { Suspense } from 'react'

import { VariantSelector } from './VariantSelector'
import { StockIndicator } from '@/components/product/StockIndicator'
import { SizeChartDialog } from '@/components/product/SizeChartDialog'

export function ProductDescription({ product }: { product: Product }) {
  const hasVariants = Boolean(product.enableVariants && product.variants?.docs?.length)

  // Цена: у товара с вариантами показываем диапазон, если он есть
  let amount = 0
  let lowestAmount = 0
  let highestAmount = 0

  if (hasVariants) {
    const prices = (product.variants?.docs ?? [])
      .filter((v): v is Variant => typeof v === 'object' && v !== null)
      .map((v) => v.priceInRUB)
      .filter((p): p is number => typeof p === 'number')
    if (prices.length) {
      lowestAmount = Math.min(...prices)
      highestAmount = Math.max(...prices)
    }
  } else if (typeof product.priceInRUB === 'number') {
    amount = product.priceInRUB
  }

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <h1 className="font-display text-[clamp(1.6rem,3.5vw,2.75rem)] leading-[1.05] text-ink">{product.title}</h1>
        <div className="text-2xl text-ink md:text-3xl">
          {hasVariants ? <Price highestAmount={highestAmount} lowestAmount={lowestAmount} /> : <Price amount={amount} />}
        </div>
      </div>

      {product.description ? (
        <div className="[&_p]:text-ink/75 [&_p]:leading-relaxed [&_p+p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5">
          <RichText data={product.description} enableGutter={false} enableProse={false} />
        </div>
      ) : null}

      {hasVariants && (
        <div className="flex flex-col gap-5 border-t border-ink/10 pt-6">
          <Suspense fallback={null}>
            <VariantSelector product={product} />
          </Suspense>
          {product.sizeChart && typeof product.sizeChart === 'object' && <SizeChartDialog chart={product.sizeChart} />}
        </div>
      )}

      <div className="flex flex-col gap-4 border-t border-ink/10 pt-6">
        <Suspense fallback={null}>
          <StockIndicator product={product} />
        </Suspense>
        <Suspense fallback={null}>
          <AddToCart product={product} />
        </Suspense>
      </div>

      <ul className="flex flex-col gap-2 text-sm text-ink/60">
        <li>Забрать можно на тренировке — договоримся в переписке.</li>
        <li>Доставку посчитаем отдельно и сообщим до отправки.</li>
      </ul>
    </div>
  )
}
